import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { residences } from "@/data/residences";
import heroImage from "@/assets/ibiza-residences-hero.webp";
import villaInterior from "@/assets/villa-interior.webp";

const ease = [0.22, 1, 0.36, 1] as const;

const IbizaResidences = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />

      {/* Hero */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Arzani Residences Ibiza" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-foreground/10" />
        </div>
        <div className="relative z-10 px-8 md:px-16 lg:px-20 pb-20 md:pb-28 max-w-2xl">
          <motion.span
            className="font-label text-primary-foreground/40 tracking-[0.3em] text-[9px] block mb-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 2, ease }}
          >
            Ibiza · Season 2026
          </motion.span>
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground leading-[1] tracking-wide uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 2, ease }}
          >
            Ibiza Residences
          </motion.h1>
          <motion.p
            className="font-body text-primary-foreground/50 text-sm leading-[1.9] max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 2, ease }}
          >
            A curated selection of exceptional villas and refined private accommodations.
          </motion.p>
        </div>
      </section>

      {/* Intro */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Private Stays</span>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9]">
              Discover refined residences across Ibiza, offering privacy, premium locations, and
              thoughtfully arranged stays tailored entirely to your preferences. Each villa has been
              personally selected to meet the highest standards of comfort and distinction.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Residences Grid */}
      <section className="pb-20 md:pb-32 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {residences.map((residence, i) => (
              <motion.div
                key={residence.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 1, delay: i * 0.1, ease }}
              >
                <Link to={`/residences/${residence.slug}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden mb-5">
                    <img
                      src={residence.image}
                      alt={residence.name}
                      className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="font-display-medium text-base md:text-lg text-foreground tracking-wide uppercase mb-1">
                    {residence.name}
                  </h3>
                  <p className="font-accent text-muted-foreground text-sm italic mb-2">
                    {residence.location}
                  </p>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-label text-bronze text-xs">From {residence.pricing.low}/week</span>
                    <span className="font-body text-muted-foreground text-xs">· {residence.bedrooms} bedrooms</span>
                    <span className="font-body text-muted-foreground text-xs">· {residence.guests}</span>
                  </div>
                  <span className="font-label text-foreground/60 text-[10px] border-b border-foreground/20 pb-0.5 group-hover:text-foreground group-hover:border-foreground/50 transition-colors duration-700">
                    View details
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Private Villas Banner */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src={villaInterior} alt="Private villa interior" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-foreground/60" />
        </div>
        <div className="relative z-10 container-luxury max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <h2 className="font-display text-3xl md:text-5xl text-primary-foreground mb-8 tracking-wide uppercase">
              Private Villas
            </h2>
            <p className="font-body text-primary-foreground/70 text-sm md:text-base leading-[1.9] mb-4">
              Beyond our current selection, a wider collection of exceptional villas is available upon request.
            </p>
            <p className="font-body text-primary-foreground/70 text-sm md:text-base leading-[1.9] mb-4">
              For those seeking something specific — a secluded retreat, a coastal residence, or a villa designed for gathering — our team will carefully curate options tailored to your preferences.
            </p>
            <p className="font-body text-primary-foreground/70 text-sm md:text-base leading-[1.9] mb-10">
              Simply contact us, and we will present the possibilities.
            </p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-primary-foreground/40 text-primary-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-300"
            >
              Get in touch
            </a>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-spacing bg-warm-black">
        <div className="container-luxury max-w-4xl">
          <motion.div
            className="text-center mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">How It Works</span>
            <h2 className="font-display text-3xl md:text-5xl text-primary-foreground tracking-wide uppercase">
              Residence Requests
            </h2>
            <p className="font-body text-primary-foreground/40 text-sm mt-4">
              A simple process, designed with ease in mind.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {[
              { step: "1", title: "Send your request", text: "Share your preferred dates, number of guests, and budget with our team. This allows us to immediately search for the most suitable options." },
              { step: "2", title: "Review your options", text: "We present a curated selection of villas tailored to your preferences. Once you've chosen, a deposit secures the booking." },
              { step: "3", title: "Enjoy the residence", text: "Everything is arranged before your arrival. Step through the door, settle in, and let the island reveal itself at your pace." },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="text-center md:text-left"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.15, ease }}
              >
                <span className="font-display text-5xl md:text-6xl text-primary-foreground/10 block mb-4">
                  {item.step}
                </span>
                <h3 className="font-display-medium text-lg text-primary-foreground tracking-wide uppercase mb-4">
                  {item.title}
                </h3>
                <p className="font-body text-primary-foreground/50 text-sm leading-[1.9]">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-6xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">FAQ</span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground tracking-wide uppercase">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease }}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-12"
          >
            {(() => {
              const faqItems = [
                { q: "How does booking a residence through Arzani work?", a: "Once you submit your request, our team will present a curated selection of residences based on your preferences. After your selection, we secure the booking and arrange all necessary details for your stay." },
                { q: "What types of residences are available?", a: "We offer a range of private villas and luxury residences, from modern hillside homes to waterfront properties in prime locations." },
                { q: "What is included in a residence booking?", a: "Most residences include standard amenities such as cleaning, utilities, and property management support. Additional services can be arranged upon request." },
                { q: "Can additional services be arranged for my stay?", a: "Yes. We can arrange housekeeping, private chefs, chauffeurs, security, and other bespoke services to enhance your stay." },
                { q: "Is a deposit required to secure a residence?", a: "Yes. A deposit is required to confirm and secure the booking. Payment terms vary depending on the property and will be communicated in advance." },
                { q: "What is the minimum stay?", a: "Minimum stay requirements vary per property, especially during high season. This will be confirmed during the booking process." },
                { q: "Are the residences private?", a: "Yes. All residences are fully private and selected for comfort, discretion, and high standards." },
                { q: "What happens in case of cancellation?", a: "Cancellation policies vary depending on the property. Full details will be provided before confirming your booking." },
                { q: "Can you arrange last-minute bookings?", a: "Subject to availability, we can assist with last-minute requests and will always aim to find the most suitable option." },
                { q: "Where are the residences located?", a: "We offer residences in carefully selected locations, including Ibiza, Marbella, Saint-Tropez, Dubai, and other key destinations." },
              ];
              const left = faqItems.slice(0, 5);
              const right = faqItems.slice(5);
              return (
                <>
                  <Accordion type="single" collapsible className="w-full">
                    {left.map((item, i) => (
                      <AccordionItem key={i} value={`faq-${i}`} className="border-foreground/10">
                        <AccordionTrigger className="font-display-medium text-sm md:text-base text-foreground tracking-wide uppercase text-left hover:no-underline py-5">{item.q}</AccordionTrigger>
                        <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.9]">{item.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  <Accordion type="single" collapsible className="w-full">
                    {right.map((item, i) => (
                      <AccordionItem key={i} value={`faq-r-${i}`} className="border-foreground/10">
                        <AccordionTrigger className="font-display-medium text-sm md:text-base text-foreground tracking-wide uppercase text-left hover:no-underline py-5">{item.q}</AccordionTrigger>
                        <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.9]">{item.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </>
              );
            })()}
          </motion.div>

          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <p className="font-display-medium text-lg md:text-xl text-foreground tracking-wide uppercase mb-4">
              Still have a question?
            </p>
            <p className="font-body text-muted-foreground text-sm leading-[1.9] mb-8">
              Our team is happy to help with anything you need.
            </p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact */}
      <section id="residence-inquire" className="section-spacing bg-background">
        <div className="container-luxury max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Get In Touch</span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground tracking-wide uppercase mb-6">
              Request a Residence
            </h2>
            <p className="font-body text-muted-foreground text-sm leading-[1.9] mb-10">
              Send us a message with your preferred villa, dates, and any special requests.
              Our team will respond promptly with availability and a tailored proposal.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/message/2XEKJ5STR2IZJ1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
              >
                WhatsApp
              </a>
              <a
                href="mailto:info@arzanigroupworldwide.com"
                className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
              >
                Email Us
              </a>
            </div>
            <p className="font-body text-muted-foreground/60 text-xs mt-8">
              +31 6 19038233 · info@arzanigroupworldwide.com
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IbizaResidences;
